import React, { useState } from "react";
import {
  FaCheckCircle,
  FaDownload,
  FaExclamationTriangle,
  FaFileExcel,
  FaInfoCircle,
  FaTimes,
  FaUpload,
} from "react-icons/fa";
import Modal from "react-modal";
import { toast } from "react-toastify";
import { useImportEntreesExcelMutation } from "../../actions/ventes/importMutation";
import ExcelDropzone from "../../components/FileUpload/ExcelDropzone";
import ImportResultsDisplay from "../../components/ImportResults/ImportResultsDisplay";
import { generateEntreesTemplate } from "../../utils/excelTemplate";
import "./ModalImportEntrees.css";

Modal.setAppElement("#root");

const ModalImportEntrees = ({ isOpen, onClose, dossierId, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [results, setResults] = useState(null);
  const [showInstructions, setShowInstructions] = useState(true);

  const [importEntrees, { isLoading: loading }] = useImportEntreesExcelMutation();

  const handleFileSelect = (selectedFile) => {
    setFile(selectedFile);
    setResults(null);
  };

  const handleDownloadTemplate = () => {
    try {
      generateEntreesTemplate();
      toast.success("Template téléchargé avec succès");
    } catch {
      toast.error("Erreur lors du téléchargement du template");
    }
  };

  /**
   * Normalise n'importe quelle réponse Django en un objet uniforme.
   *
   * Mapping des clés backend → frontend :
   *   data.errors          → parsing_errors   (erreurs ligne par ligne au parsing)
   *   data.erreurs         → erreurs_creation (erreurs lors de la création en base)
   *   data.lignes_ignorees → lignes_ignorees
   *   data.aide            → aide
   */
  const normalizeResponse = (data = {}) => ({
    success:          data.success          ?? false,
    message:          data.message          ?? "",
    entrees_creees:   data.entrees_creees   ?? 0,
    produits_crees:   data.produits_crees   ?? 0,
    parsing_errors:   data.errors           ?? [],   // ← vient de parse_excel_file
    lignes_ignorees:  data.lignes_ignorees  ?? [],
    erreurs_creation: data.erreurs          ?? [],   // ← vient de create_entrees_masse
    aide:             data.aide             ?? null,
  });

  /**
   * Extrait les données Django depuis une erreur RTK Query.
   * RTK Query place la réponse dans error.data (fetchBaseQuery)
   * ou error.error.data (axiosBaseQuery).
   */

  const handleImport = async () => {
    if (!file) {
      setResults({
        success: false,
        message: "Veuillez sélectionner un fichier Excel.",
        entrees_creees: 0,
        produits_crees: 0,
        parsing_errors: [],
        erreurs_creation: [],
      });
      return;
    }
    if (!dossierId) {
      setResults({
        success: false,
        message: "Identifiant du dossier manquant.",
        entrees_creees: 0,
        produits_crees: 0,
        parsing_errors: [],
        erreurs_creation: [],
      });
      return;
    }

    setResults(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("dossier_id", dossierId);

      const response = await importEntrees(formData).unwrap();
      const normalized = normalizeResponse(response);
      setResults(normalized);

      if (normalized.success) {
        setTimeout(() => { if (onSuccess) onSuccess(); }, 1500);
      }
    } catch (error) {
      const apiData = error?.data || error?.error?.data || {};
      const normalized = normalizeResponse(apiData);
      setResults(normalized);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResults(null);
    setShowInstructions(false);
    onClose();
  };

  const handleCloseWithRefresh = () => {
    if (results?.entrees_creees > 0 && onSuccess) onSuccess();
    handleClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      className="modal-import-entrees"
      overlayClassName="modal-import-overlay"
      contentLabel="Importer des entrées"
    >
      <div className="modal-import-container">

        {/* ── En-tête ─────────────────────────────────────────────── */}
        <div className="modal-import-header">
          <div className="header-content">
            <div className="header-icon"><FaFileExcel /></div>
            <div className="header-text">
              <h2>Importer des entrées</h2>
              <p>Importez plusieurs entrées en une seule fois via Excel</p>
            </div>
          </div>
          <button type="button" className="btn-close-modal" onClick={handleClose} disabled={loading}>
            <FaTimes />
          </button>
        </div>

        {/* ── Contenu ─────────────────────────────────────────────── */}
        <div className="modal-import-content">

          {/* Étape 1 - Template */}
          <div className="import-section">
            <div className="section-header">
              <div className="section-number">1</div>
              <h3>Télécharger le modèle Excel</h3>
            </div>
            <p className="section-description">
              Téléchargez le fichier modèle, remplissez-le avec vos données, puis importez-le.
            </p>
            <div className="section-actions">
              <button type="button" className="btn-download-template" onClick={handleDownloadTemplate} disabled={loading}>
                <FaDownload /><span>Télécharger le modèle</span>
              </button>
              <button type="button" className="btn-show-instructions" onClick={() => setShowInstructions(!showInstructions)}>
                <FaInfoCircle />
                <span>{showInstructions ? "Masquer" : "Voir"} les instructions</span>
              </button>
            </div>

            {showInstructions && (
              <div className="instructions-panel">
                <h4><FaInfoCircle /> Instructions d&apos;import</h4>
                <div className="instructions-content">
                  {[
                    { titre: "Champs obligatoires",   texte: "Date, Intitulé, Client, Nom produit, Description, Quantité (> 0), Prix unitaire (> 0)." },
                    { titre: "Compte comptable",      texte: "Optionnel (ex: 41110025). S'il n'est pas renseigné, le compte du client est incrémenté automatiquement." },
                    { titre: "Taxes",                 texte: "Valeurs acceptées uniquement : 0 %, 10 % ou 18 %." },
                    { titre: "Sources de paiement",   texte: "Valeurs acceptées : banque, caisse, wave, orange_money." },
                    { titre: "Montant payé",          texte: "Ne peut pas dépasser Quantité × Prix unitaire par ligne, ni le total TTC de l'entrée." },
                    { titre: "Format des dates",      texte: "Format JJ/MM/AAAA (exemple : 15/01/2026)." },
                    { titre: "Groupement", texte: "Les lignes avec la même Date + Intitulé + Client sont fusionnées en une seule entrée. Même produit et même prix : quantités fusionnées. Même produit et prix différents : import bloqué." },
                    { titre: "Règle tout ou rien", texte: "Si une seule ligne est invalide, aucune entrée n'est créée. Toutes les erreurs s'affichent dans ce modal." },
                    { titre: "Clients nouveaux",      texte: "Les clients absents de la base sont créés automatiquement." },
                  ].map(({ titre, texte }) => (
                    <div className="instruction-item" key={titre}>
                      <FaCheckCircle className="check-icon" />
                      <div><strong>{titre} :</strong><p>{texte}</p></div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Étape 2 - Upload (masqué quand on a des résultats) */}
          {!results && (
            <div className="import-section">
              <div className="section-header">
                <div className="section-number">2</div>
                <h3>Sélectionner votre fichier</h3>
              </div>
              <p className="section-description">
                Glissez-déposez votre fichier Excel ou cliquez pour le sélectionner.
              </p>
              <ExcelDropzone onFileSelect={handleFileSelect} />
            </div>
          )}

          {/* Résultats - toujours via ImportResultsDisplay, jamais de bannière générique */}
          {results && (
            <div className="import-section">
              <div className="section-header">
                <div className="section-number">
                  {results.success
                    ? <FaCheckCircle style={{ color: "#22c55e" }} />
                    : <FaExclamationTriangle style={{ color: "#ef4444" }} />
                  }
                </div>
                <h3>Résultats de l&apos;import</h3>
              </div>
              <ImportResultsDisplay
                results={results}
                onClose={handleCloseWithRefresh}
                entityLabel="entrée"
              />
            </div>
          )}
        </div>

        {/* ── Pied de page ────────────────────────────────────────── */}
        {!results && (
          <div className="modal-import-footer">
            <button type="button" className="btn-cancel" onClick={handleClose} disabled={loading}>
              Annuler
            </button>
            {file && (
              <button type="button" className="btn-import" onClick={handleImport} disabled={loading}>
                {loading
                  ? <><span className="spinner" /><span>Import en cours...</span></>
                  : <><FaUpload /><span>Importer</span></>
                }
              </button>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ModalImportEntrees;